// Landing pages · detalle: estado, métricas, Publicación, Conversion Funnel, Analytics y Visitantes.
import type { JSX } from "react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Copy, ExternalLink, Filter, Monitor, Pencil, Smartphone, Tablet } from "lucide-react";
import { api, ApiError } from "../../../lib/api.ts";
import { useMe } from "../../../lib/auth.ts";
import { formatDateTime } from "../../../lib/format.ts";
import { Button, Card, EmptyState, ErrorState, Field, Input, Loading, Select, TableSkeleton } from "../../../components/ui.tsx";
import { toast } from "../../../components/toast.tsx";
import { Donut, type LandingDetailData, LineChart, StatusToggle, copyText, pct } from "./shared.tsx";

type Tab = "pub" | "funnel" | "analytics" | "visitors";
const TABS: Array<{ k: Tab; label: string }> = [
  { k: "pub", label: "Publicación" }, { k: "funnel", label: "Conversion Funnel" }, { k: "analytics", label: "Analytics" }, { k: "visitors", label: "Visitantes" },
];

interface Range { days: number | null; from: string; to: string }
const rangeQs = (r: Range): string => (r.days ? `days=${r.days}` : `from=${r.from}&to=${r.to}`);

function RangePicker({ value, onChange }: { value: Range; onChange: (r: Range) => void }): JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Select value={value.days ?? "custom"} className="w-auto" onChange={(e) => {
        const v = e.target.value;
        onChange(v === "custom" ? { ...value, days: null } : { ...value, days: Number(v) });
      }}>
        {[7, 30, 60, 90].map((d) => <option key={d} value={d}>Últimos {d} días</option>)}
        <option value="custom">Personalizado</option>
      </Select>
      {value.days == null && (
        <>
          <Input type="date" value={value.from} className="w-auto" onChange={(e) => onChange({ ...value, from: e.target.value })} />
          <span className="text-ink-muted text-sm">a</span>
          <Input type="date" value={value.to} className="w-auto" onChange={(e) => onChange({ ...value, to: e.target.value })} />
        </>
      )}
    </div>
  );
}

export function LandingDetail(): JSX.Element {
  const { id = "" } = useParams();
  const me = useMe();
  const admin = Boolean(me.data?.admin);
  const nav = useNavigate();
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>("pub");
  const today = new Date().toISOString().slice(0, 10);
  const [range, setRange] = useState<Range>({ days: 30, from: new Date(Date.now() - 29 * 86400_000).toISOString().slice(0, 10), to: today });

  const q = useQuery<LandingDetailData>({ queryKey: ["mkt-landing", id], queryFn: () => api.get(`/mkt/landings/${id}`) });
  const onErr = (e: unknown): void => toast.error(e instanceof ApiError ? e.message : "No se pudo completar la acción.");
  const patch = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.patch(`/mkt/landings/${id}`, body),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["mkt-landing", id] }); void qc.invalidateQueries({ queryKey: ["mkt-landings"] }); toast.success("Cambios guardados."); },
    onError: onErr,
  });

  if (q.isLoading) return <Loading />;
  if (q.isError || !q.data) return <ErrorState message="No se pudo cargar la landing." onRetry={() => void q.refetch()} />;
  const l = q.data;
  const published = l.status === "published";

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <Link to=".." relative="path" className="btn-ghost btn-sm" aria-label="Volver"><ArrowLeft size={16} /></Link>
          <h2 className="text-lg font-semibold text-ink truncate">{l.name}</h2>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <StatusToggle published={published} disabled={!admin || patch.isPending} onChange={(v) => patch.mutate({ status: v ? "published" : "draft" })} />
          {l.funnels.map((f) => (
            <Button key={f.id} small onClick={() => nav(`/marketing/embudos/${f.id}`)}><Filter size={14} /> Ir a embudo{l.funnels.length > 1 ? `: ${f.name}` : ""}</Button>
          ))}
          {admin && <Button variant="primary" small onClick={() => nav("editor")}><Pencil size={14} /> Editar diseño</Button>}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card className="p-4">
          <div className="dt-label">Visitantes | Visitas</div>
          <div className="text-2xl font-semibold text-ink tnum mt-1">{l.uniqueVisitors} <span className="text-ink-muted font-normal">|</span> {l.visits}</div>
        </Card>
        <Card className="p-4">
          <div className="dt-label">Conversiones</div>
          <div className="text-2xl font-semibold text-ink tnum mt-1">{l.conversions}</div>
          <div className="text-xs text-ink-muted">Envíos de formularios incrustados en la landing</div>
        </Card>
        <Card className="p-4 flex items-center gap-4">
          <Donut value={l.conversions} total={l.uniqueVisitors} label="conversión" size={92} />
          <div className="text-sm text-ink-secondary">Tasa de conversión sobre visitantes únicos</div>
        </Card>
      </div>

      <nav className="flex gap-1 border-b border-line overflow-x-auto scrollbar-thin">
        {TABS.map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)}
            className={`px-3 py-2 text-sm whitespace-nowrap border-b-2 -mb-px ${tab === t.k ? "border-primary text-primary font-medium" : "border-transparent text-ink-secondary hover:text-ink"}`}>{t.label}</button>
        ))}
      </nav>

      {tab === "pub" && <PublicationTab l={l} admin={admin} onSave={(b) => patch.mutate(b)} saving={patch.isPending} />}
      {tab === "funnel" && <FunnelTab id={id} range={range} setRange={setRange} />}
      {tab === "analytics" && <AnalyticsTab id={id} range={range} setRange={setRange} />}
      {tab === "visitors" && <VisitorsTab id={id} />}
    </div>
  );
}

function PublicationTab({ l, admin, onSave, saving }: { l: LandingDetailData; admin: boolean; onSave: (b: Record<string, unknown>) => void; saving: boolean }): JSX.Element {
  const [slug, setSlug] = useState(l.slug);
  const [folder, setFolder] = useState(l.folder ?? "");
  const [formId, setFormId] = useState(l.formId ?? "");
  const [slugMsg, setSlugMsg] = useState<{ ok: boolean; message: string } | null>(null);
  useEffect(() => { setSlug(l.slug); setFolder(l.folder ?? ""); setFormId(l.formId ?? ""); }, [l.slug, l.folder, l.formId]);
  const forms = useQuery<{ rows: Array<{ id: string; name: string; status: string }> }>({ queryKey: ["mkt-landing-forms"], queryFn: () => api.get("/mkt/landings/form-options") });

  useEffect(() => {
    if (slug === l.slug) { setSlugMsg(null); return; }
    const t = setTimeout(() => {
      void api.get<{ ok: boolean; message: string }>(`/mkt/landings/check-slug?slug=${encodeURIComponent(slug)}&id=${l.id}`).then(setSlugMsg).catch(() => setSlugMsg(null));
    }, 350);
    return () => clearTimeout(t);
  }, [slug, l.slug, l.id]);

  const published = l.status === "published";
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card className="p-5 space-y-3">
        <h3 className="sec-title">URL pública</h3>
        <div className="flex gap-2">
          <Input readOnly value={l.publicUrl} onFocus={(e) => e.currentTarget.select()} />
          <Button onClick={() => void copyText(l.publicUrl).then((ok) => (ok ? toast.success("URL copiada.") : toast.error("No se pudo copiar.")))}><Copy size={14} /> Copiar</Button>
          <a className={`btn-secondary ${published ? "" : "pointer-events-none opacity-50"}`} href={l.publicUrl} target="_blank" rel="noopener noreferrer"><ExternalLink size={14} /> Ver</a>
        </div>
        {!published && <p className="text-xs text-warning">La landing está en borrador: la URL pública responde «no disponible» hasta publicarla.</p>}
        {l.externalUrl && <p className="text-xs text-ink-muted">Importada desde <span className="break-all">{l.externalUrl}</span></p>}
      </Card>
      <Card className="p-5 space-y-3">
        <h3 className="sec-title">Configuración</h3>
        <Field label="Slug (dirección)" hint={slugMsg ? undefined : "a-z, 0-9 y guiones; único en Tiger."} error={slugMsg && !slugMsg.ok ? slugMsg.message : undefined}>
          <Input value={slug} disabled={!admin} maxLength={80} onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))} />
        </Field>
        <Field label="Carpeta"><Input value={folder} disabled={!admin} maxLength={80} onChange={(e) => setFolder(e.target.value)} placeholder="Sin carpeta" /></Field>
        <Field label="Formulario principal" hint="Se usa en los huecos de formularios de páginas importadas. El bloque Formulario del editor también lo asigna.">
          <Select value={formId} disabled={!admin} onChange={(e) => setFormId(e.target.value)}>
            <option value="">— Ninguno —</option>
            {(forms.data?.rows ?? []).map((f) => <option key={f.id} value={f.id}>{f.name}{f.status !== "published" ? " (borrador: no se mostrará)" : ""}</option>)}
          </Select>
        </Field>
        {admin && (
          <div className="flex justify-end">
            <Button variant="primary" disabled={saving || (slugMsg != null && !slugMsg.ok) || !/^[a-z0-9-]{3,80}$/.test(slug)}
              onClick={() => onSave({ ...(slug !== l.slug ? { slug } : {}), folder: folder.trim() || null, formId: formId || null })}>Guardar</Button>
          </div>
        )}
      </Card>
    </div>
  );
}

interface FunnelRow { source: string; label: string; visitors: number; contacts: number; opportunities: number; sales: number; amount: number }

function FunnelTab({ id, range, setRange }: { id: string; range: Range; setRange: (r: Range) => void }): JSX.Element {
  const q = useQuery<{ rows: FunnelRow[] }>({ queryKey: ["mkt-landing-funnel", id, rangeQs(range)], queryFn: () => api.get(`/mkt/landings/${id}/funnel?${rangeQs(range)}`) });
  const rows = q.data?.rows ?? [];
  const tot = rows.reduce((a, r) => ({ visitors: a.visitors + r.visitors, contacts: a.contacts + r.contacts, opportunities: a.opportunities + r.opportunities, sales: a.sales + r.sales }), { visitors: 0, contacts: 0, opportunities: 0, sales: 0 });
  const max = Math.max(1, tot.contacts);
  return (
    <Card className="p-5 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="sec-title">Contactos → oportunidades → ventas por origen</h3>
        <RangePicker value={range} onChange={setRange} />
      </div>
      {q.isLoading ? <TableSkeleton rows={6} cols={5} /> : (
        <>
          <div className="grid grid-cols-3 gap-3">
            {[["Contactos", tot.contacts], ["Oportunidades", tot.opportunities], ["Ventas", tot.sales]].map(([k, v], i) => (
              <div key={k} className="rounded-md bg-primary-soft p-3 text-center" style={{ opacity: 1 - i * 0.12 }}>
                <div className="text-xs text-ink-secondary">{k}</div>
                <div className="text-2xl font-semibold text-primary tnum">{v}</div>
                {i > 0 && <div className="text-xs text-ink-muted">{pct(Number(v), i === 1 ? tot.contacts : tot.opportunities)} del paso anterior</div>}
              </div>
            ))}
          </div>
          <div className="overflow-x-auto scrollbar-thin">
            <table className="w-full">
              <thead><tr><th className="th">Origen</th><th className="th text-right">Visitantes</th><th className="th">Contactos</th><th className="th text-right">Oportunidades</th><th className="th text-right">Ventas</th></tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.source}>
                    <td className="td font-medium text-ink whitespace-nowrap">{r.label}</td>
                    <td className="td text-right tnum">{r.visitors}</td>
                    <td className="td min-w-[160px]">
                      <div className="flex items-center gap-2">
                        <div className="h-2 rounded-full bg-primary" style={{ width: `${(r.contacts / max) * 100}%`, minWidth: r.contacts ? 4 : 0 }} />
                        <span className="tnum">{r.contacts}</span>
                      </div>
                    </td>
                    <td className="td text-right tnum">{r.opportunities} <span className="text-ink-muted text-xs">({pct(r.opportunities, r.contacts)})</span></td>
                    <td className="td text-right tnum">{r.sales} <span className="text-ink-muted text-xs">({pct(r.sales, r.opportunities)})</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-ink-muted">Contactos que se convirtieron en la landing (envío de formulario) y sus oportunidades creadas desde entonces; ventas = oportunidades en etapa ganada.</p>
        </>
      )}
    </Card>
  );
}

interface Analytics { days: Array<{ day: string; views: number; sessions: number; visitors: number; conversions: number }>; totals: { views: number; visitors: number; sessions: number; conversions: number }; devices: Array<{ device: string; n: number }> }
const DEVICE_LABEL: Record<string, string> = { desktop: "Escritorio", mobile: "Móvil", tablet: "Tablet" };

function AnalyticsTab({ id, range, setRange }: { id: string; range: Range; setRange: (r: Range) => void }): JSX.Element {
  const q = useQuery<Analytics>({ queryKey: ["mkt-landing-analytics", id, rangeQs(range)], queryFn: () => api.get(`/mkt/landings/${id}/analytics?${rangeQs(range)}`) });
  const d = q.data;
  const devTotal = (d?.devices ?? []).reduce((a, x) => a + x.n, 0);
  return (
    <Card className="p-5 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="sec-title">Visitas por día</h3>
        <RangePicker value={range} onChange={setRange} />
      </div>
      {q.isLoading || !d ? <TableSkeleton rows={4} cols={4} /> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[["Visualizaciones", d.totals.views], ["Sesiones", d.totals.sessions], ["Visitantes", d.totals.visitors], ["Conversiones", d.totals.conversions]].map(([k, v]) => (
              <div key={k} className="rounded-md border border-line p-3"><div className="dt-label">{k}</div><div className="text-xl font-semibold text-ink tnum">{v}</div></div>
            ))}
          </div>
          <LineChart labels={d.days.map((x) => x.day)} series={[
            { name: "Visualizaciones", color: "var(--primary)", values: d.days.map((x) => x.views) },
            { name: "Visitantes", color: "var(--accent)", values: d.days.map((x) => x.visitors) },
            { name: "Conversiones", color: "var(--success)", values: d.days.map((x) => x.conversions) },
          ]} />
          {devTotal > 0 && (
            <div className="flex flex-wrap gap-4 text-sm text-ink-secondary">
              {d.devices.map((x) => <span key={x.device}>{DEVICE_LABEL[x.device] ?? x.device}: <span className="tnum text-ink">{pct(x.n, devTotal)}</span></span>)}
            </div>
          )}
          <p className="text-xs text-ink-muted">Sesión = visitante único por día (no se usan cookies). Tiempo real, mapas de calor y grabaciones no están disponibles.</p>
        </>
      )}
    </Card>
  );
}

interface Visitor { createdAt: string; device: string | null; sourceLabel: string; referrer: string | null; utm: Record<string, string> | null; contactId: string | null; contactName: string | null; contactEmail: string | null }

function DeviceIcon({ d }: { d: string | null }): JSX.Element {
  const I = d === "mobile" ? Smartphone : d === "tablet" ? Tablet : Monitor;
  return <span className="inline-flex items-center gap-1.5"><I size={14} strokeWidth={1.75} /> {DEVICE_LABEL[d ?? "desktop"] ?? d}</span>;
}

function VisitorsTab({ id }: { id: string }): JSX.Element {
  const q = useQuery<{ rows: Visitor[] }>({ queryKey: ["mkt-landing-visitors", id], queryFn: () => api.get(`/mkt/landings/${id}/visitors`) });
  if (q.isLoading) return <Card><TableSkeleton rows={6} cols={4} /></Card>;
  const rows = q.data?.rows ?? [];
  if (!rows.length) return <Card><EmptyState title="Sin visitas todavía" body="Cuando la landing esté publicada y reciba visitas, aparecerán aquí." /></Card>;
  return (
    <Card className="overflow-hidden">
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full">
          <thead><tr><th className="th">Fecha</th><th className="th">Dispositivo</th><th className="th">Origen</th><th className="th">Contacto</th></tr></thead>
          <tbody>
            {rows.map((v, i) => (
              <tr key={i}>
                <td className="td whitespace-nowrap">{formatDateTime(v.createdAt)}</td>
                <td className="td whitespace-nowrap"><DeviceIcon d={v.device} /></td>
                <td className="td">
                  <div>{v.sourceLabel}</div>
                  {(v.referrer || v.utm) && <div className="text-xs text-ink-muted truncate max-w-xs">{v.utm?.["utm_campaign"] ? `utm: ${v.utm["utm_source"] ?? ""} / ${v.utm["utm_campaign"]}` : v.referrer}</div>}
                </td>
                <td className="td">{v.contactId ? <Link className="link" to={`/contacts/${v.contactId}`}>{v.contactName ?? v.contactEmail ?? "Contacto"}</Link> : <span className="text-ink-muted">Anónimo</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
